import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import type { OfflineSourceResponse, OfflineVerifyResponse } from './types';

/** Thrown when the offline-video edge function answers with an error status. */
export class OfflineApiError extends Error {
  status: number;
  reason: string | null;
  constructor(message: string, status: number, reason: string | null = null) {
    super(message);
    this.name = 'OfflineApiError';
    this.status = status;
    this.reason = reason;
  }
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>('offline-video', { body });
  if (error) {
    // FunctionsHttpError carries the Response in `context`; read the status
    // and JSON body so callers can distinguish 403 (paywall) from 422/5xx.
    const response = (error as { context?: Response }).context;
    let status = 0;
    let message = error.message || 'Request failed';
    let reason: string | null = null;
    if (response && typeof response.status === 'number') {
      status = response.status;
      try {
        const json = await response.json();
        if (json?.error) message = String(json.error);
        if (json?.reason) reason = String(json.reason);
      } catch {
        // non-JSON body — keep the generic message
      }
    }
    logger.warn('[offline] offline-video call failed', { action: body.action, status, message, reason });
    throw new OfflineApiError(message, status, reason);
  }
  if (!data) throw new OfflineApiError('Empty response', 0);
  return data;
}

export function requestOfflineSource(lessonVideoId: number, quality = '720p'): Promise<OfflineSourceResponse> {
  return invoke<OfflineSourceResponse>({ action: 'source', lesson_video_id: lessonVideoId, quality });
}

export function verifyOfflineLessons(lessonIds: number[]): Promise<OfflineVerifyResponse> {
  return invoke<OfflineVerifyResponse>({ action: 'verify', lesson_ids: lessonIds });
}
